<script>
  import { onMount, onDestroy } from 'svelte';
  import {
    createGrid,
    ModuleRegistry,
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    CustomFilterModule,
    RowStyleModule,
    RowAutoHeightModule,
    CellStyleModule,
    ColumnApiModule,
    ColumnAutoSizeModule,
    RowApiModule,
  } from 'ag-grid-community';
  import { SetFilter } from './SetFilter.js';

  // Every AG Grid Community module a colDef/gridOption used below needs - each is independently
  // tree-shaken/tied to specific features (see AG Grid's own module-validation errors, e.g.
  // "#200 ... reasonOrId=getRowClass"), so forgetting one doesn't fail the build, it silently
  // no-ops (or throws) at runtime instead. Confirmed against the installed package's own exports -
  // see ui/CONTEXT.md.
  ModuleRegistry.registerModules([
    ClientSideRowModelModule,
    TextFilterModule,
    NumberFilterModule,
    DateFilterModule,
    CustomFilterModule,
    RowStyleModule, // getRowClass
    RowAutoHeightModule, // colDef.autoHeight (Description)
    CellStyleModule, // colDef.cellStyle (Start column urgency coloring)
    ColumnApiModule, // gridApi.getColumnState()/applyColumnState()/setColumnsVisible()
    ColumnAutoSizeModule, // gridApi.autoSizeColumns() (see widenAllColumns below)
    RowApiModule, // gridApi.redrawRows() (Description click-to-expand)
  ]);

  export let events = [];
  export let onRefresh = () => {};

  let container;
  let gridApi;
  let hadPersistedColumnState = false;

  // --- Column metadata (the single source of truth for field/label/behavior, mirroring the
  // pre-AG-Grid COLUMNS array - see ui/CONTEXT.md) ---
  const COLUMNS = [
    ['accountName', 'Account'],
    ['accountId', 'Account ID'],
    ['region', 'Region'],
    ['service', 'Service'],
    ['eventTypeCategory', 'Category'],
    ['statusCode', 'Status'],
    ['description', 'Description'],
    ['eventTypeCode', 'Event type'],
    ['lastUpdatedTime', 'Updated'],
    ['startTime', 'Start'],
    ['endTime', 'End'],
    ['actionability', 'Actionability'],
  ];
  const COLUMN_LABELS = Object.fromEntries(COLUMNS);

  const DATE_FIELDS = new Set(['lastUpdatedTime', 'startTime', 'endTime']);

  // Fields with a small, enumerable set of values get the hand-rolled SetFilter (AG Grid
  // Community has no built-in agSetColumnFilter - see ui/CONTEXT.md and SetFilter.js).
  const SET_FILTER_FIELDS = new Set([
    'accountName',
    'accountId',
    'region',
    'service',
    'eventTypeCategory',
    'statusCode',
    'eventTypeCode',
  ]);

  const DEFAULT_HIDDEN_COLUMNS = new Set(['eventTypeCode', 'eventTypeCategory']);

  const COMPACT_WIDTHS = {
    accountName: 170,
    accountId: 130,
    region: 110,
    service: 110,
    eventTypeCategory: 170,
    statusCode: 110,
    eventTypeCode: 150,
    lastUpdatedTime: 150,
    startTime: 150,
    endTime: 150,
    actionability: 130,
  };

  // Every eventTypeCategory value the AWS Health API can return (botocore's health service
  // model's `eventTypeCategory` enum), mapped to a friendlier display label.
  const CATEGORY_LABELS = {
    issue: 'Issue',
    accountNotification: 'Account Notification',
    scheduledChange: 'Scheduled Change',
    investigation: 'Investigation',
  };

  function formatCategory(raw) {
    return CATEGORY_LABELS[raw] || raw;
  }

  function displayValue(raw) {
    return raw === null || raw === undefined || raw === '' ? '—' : raw;
  }

  function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n) => String(n).padStart(2, '0');
    // German date format, in the browser's own local time zone (Date's plain getters, not the
    // UTC ones, already report local time).
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  // --- Start column "how soon" coloring - logic deliberately UNCHANGED by the AG Grid migration
  // (see ui/CONTEXT.md): unstyled if more than 2 weeks out, orange if 1-2 weeks out, coral if
  // within a week (including already-started/ongoing events, a negative diff). Only the *output
  // format* changed, from a CSS string (the old plain <td style="...">) to the plain object
  // AG Grid's cellStyle expects. ---
  function daysUntilStart(iso) {
    if (!iso) return null;
    const start = new Date(iso);
    if (Number.isNaN(start.getTime())) return null;
    return (start.getTime() - Date.now()) / 86400000;
  }

  function startUrgencyBand(diffDays) {
    if (diffDays === null || diffDays > 14) return null;
    if (diffDays > 7) return 'orange'; // 1-2 weeks out
    return 'red'; // within a week, including already-started/ongoing (negative diff)
  }

  function startCellStyle(params) {
    const band = startUrgencyBand(daysUntilStart(params.data.startTime));
    // Official GroupUI secondary/accent colors + their matching "on-color" text tokens (see
    // ui/CONTEXT.md's palette) - accent-basic-default-06/on-accent-basic-default-06 (Orange) and
    // accent-basic-default-01/on-accent-basic-default-01 (Coral). Text color changed from this
    // app's own previous hand-picked white-on-coral to the design system's own paired token
    // (dark text) as part of adopting real design tokens throughout.
    if (band === 'orange') {
      return {
        background: 'var(--groupui-sys-color-accent-basic-default-06)',
        color: 'var(--groupui-sys-color-on-accent-basic-default-06)',
      };
    }
    if (band === 'red') {
      return {
        background: 'var(--groupui-sys-color-accent-basic-default-01)',
        color: 'var(--groupui-sys-color-on-accent-basic-default-01)',
      };
    }
    return null;
  }

  // --- Description truncation + click-to-expand row (a Health event's description can be long)
  // ---
  let expandedRows = new Set();

  function rowKey(row) {
    return `${row.accountId}|${row.arn}`;
  }

  function firstSentences(text, count = 1) {
    if (!text) return text;
    const matches = text.match(/[^.!?]+[.!?]+(\s|$)/g);
    if (!matches || matches.length <= count) return text.trim();
    return matches.slice(0, count).join('').trim();
  }

  // A plain-function cell renderer (returning a string) - reads the outer `expandedRows` binding
  // directly, so it always sees the latest value even though colDefs are only rebuilt when
  // `events` changes (see buildColumnDefs/the reactive rebuild below); toggling expandedRows just
  // calls gridApi.redrawRows() for that one row instead of rebuilding the whole grid.
  function descriptionCellRenderer(params) {
    const full = params.value;
    if (!full) return '—';
    return expandedRows.has(rowKey(params.data)) ? full : firstSentences(full);
  }

  // Standard AG Grid recipe for a Date filter over a field that isn't already a JS Date (ours are
  // ISO-8601 UTC strings) - compares at day (midnight) granularity, since the filter's own date
  // picker only ever picks a day.
  function dateComparator(filterLocalDateAtMidnight, cellValue) {
    if (!cellValue) return -1;
    const cellDate = new Date(cellValue);
    const cellDateAtMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate());
    if (cellDateAtMidnight < filterLocalDateAtMidnight) return -1;
    if (cellDateAtMidnight > filterLocalDateAtMidnight) return 1;
    return 0;
  }

  function getUniqueValues(field) {
    const vals = new Set();
    events.forEach((row) => {
      const v = row[field];
      vals.add(v === null || v === undefined || v === '' ? '(empty)' : String(v));
    });
    return [...vals].sort((a, b) => a.localeCompare(b));
  }

  function buildColumnDefs() {
    return COLUMNS.map(([field, headerName]) => {
      const def = {
        field,
        headerName,
        sortable: true,
        resizable: true,
        hide: DEFAULT_HIDDEN_COLUMNS.has(field),
      };

      if (field === 'description') {
        def.flex = 1;
        def.minWidth = 300;
        def.filter = false; // a checkbox per near-unique long paragraph would be useless UI
        def.wrapText = true;
        def.autoHeight = true; // expanding only grows this row's height, never any column's width
        def.cellRenderer = descriptionCellRenderer;
        def.cellClass = 'description-cell'; // adds pointer cursor to signal click-to-expand
        return def;
      }

      if (DATE_FIELDS.has(field)) {
        def.width = COMPACT_WIDTHS[field] || 150;
        def.suppressSizeToFit = true;
        def.filter = 'agDateColumnFilter';
        def.filterParams = { comparator: dateComparator };
        def.valueFormatter = (p) => formatDateTime(p.value);
        if (field === 'startTime') def.cellStyle = startCellStyle;
        return def;
      }

      if (SET_FILTER_FIELDS.has(field)) {
        def.width = COMPACT_WIDTHS[field] || 130;
        def.suppressSizeToFit = true;
        def.filter = SetFilter;
        def.filterParams = { values: getUniqueValues(field) };
        if (field === 'eventTypeCategory') {
          def.valueFormatter = (p) => (p.value === null || p.value === undefined || p.value === '' ? '—' : formatCategory(p.value));
          def.filterParams = { ...def.filterParams, cellRenderer: (raw) => formatCategory(raw) };
        } else {
          def.valueFormatter = (p) => displayValue(p.value);
        }
        return def;
      }

      // actionability (always empty in practice - see ui/CONTEXT.md's Known caveats) and any
      // other plain field.
      def.filter = true;
      def.valueFormatter = (p) => displayValue(p.value);
      return def;
    });
  }

  // --- Quick filters: one-click shortcuts, additive-toggle on top of the grid's own filter model
  // (mirrors the pre-AG-Grid design - see ui/CONTEXT.md) ---
  const QUICK_FILTERS = [
    { field: 'service', label: 'RDS', match: (v) => v === 'RDS' },
    { field: 'service', label: 'ECS', match: (v) => v === 'ECS' },
  ];

  // "Project" = the account name without its trailing stage - the first two hyphen-separated
  // tokens (e.g. "WLTP-M-TEST"/"WLTP-M-QS"/"WLTP-M-PROD" all share project "WLTP-M") - see
  // ui/CONTEXT.md for why this split point was chosen over other candidates.
  function projectOf(accountName) {
    if (!accountName) return null;
    const parts = accountName.split('-');
    return parts.length <= 2 ? accountName : parts.slice(0, 2).join('-');
  }

  // Derived from whatever account names are actually present in the loaded events - not
  // hardcoded, so this never drifts from config/config.json's supplierAccountIds. Excludes the
  // '(empty)' marker for a missing/unrecognized accountName.
  $: projectQuickFilters = [...new Set(events.map((r) => projectOf(r.accountName)))]
    .filter((project) => project && project !== '(empty)')
    .sort((a, b) => a.localeCompare(b))
    .map((project) => ({
      field: 'accountName',
      label: project,
      match: (v) => projectOf(v) === project,
    }));

  $: allQuickFilters = [...QUICK_FILTERS, ...projectQuickFilters];

  function quickFilterValues(qf) {
    const vals = new Set();
    events.forEach((row) => {
      const raw = row[qf.field];
      const v = raw === null || raw === undefined || raw === '' ? '(empty)' : String(raw);
      if (qf.match(v)) vals.add(v);
    });
    return [...vals];
  }

  function allValuesForField(field) {
    return getUniqueValues(field);
  }

  // Reassigned wholesale (not mutated) on every onFilterChanged, so Svelte's reactivity picks up
  // the change wherever it's referenced directly (same fix pattern as the pre-AG-Grid quick
  // filters - see ui/CONTEXT.md's Styling section on the active-quick-filter reactivity bug).
  let filterModelSnapshot = {};
  let activeFilterCount = 0;

  function isQuickFilterActive(qf, snapshot) {
    const colModel = snapshot[qf.field];
    if (!colModel || !Array.isArray(colModel.values)) return false;
    const vals = quickFilterValues(qf);
    return vals.length > 0 && vals.every((v) => colModel.values.includes(v));
  }

  function toggleQuickFilter(qf) {
    const vals = quickFilterValues(qf);
    if (vals.length === 0 || !gridApi) return;
    const model = gridApi.getFilterModel() || {};
    const colModel = model[qf.field];
    const allVals = allValuesForField(qf.field);
    let nextValues;
    if (colModel && Array.isArray(colModel.values)) {
      const set = new Set(colModel.values);
      const active = vals.every((v) => set.has(v));
      vals.forEach((v) => (active ? set.delete(v) : set.add(v)));
      // Collapse back to "unfiltered" whenever toggling leaves nothing (or everything) selected.
      nextValues = set.size === 0 || set.size === allVals.length ? null : [...set];
    } else {
      nextValues = vals;
    }
    const nextModel = { ...model };
    if (nextValues === null) delete nextModel[qf.field];
    else nextModel[qf.field] = { values: nextValues };
    gridApi.setFilterModel(nextModel);
  }

  function clearAllFilters() {
    gridApi?.setFilterModel(null);
  }

  // --- Columns show/hide menu (AG Grid Community has no built-in Columns tool panel - that's
  // Enterprise-only - see ui/CONTEXT.md) - reordering itself is native AG Grid header drag, not
  // handled here. ---
  let columnsMenuOpen = false;
  let columnMenuState = []; // [{ field, label, hide }], in the grid's current column order

  function refreshColumnMenuState() {
    if (!gridApi) return;
    columnMenuState = gridApi
      .getColumnState()
      .filter((s) => COLUMN_LABELS[s.colId])
      .map((s) => ({ field: s.colId, hide: !!s.hide, label: COLUMN_LABELS[s.colId] }));
  }

  function toggleColumnVisible(field) {
    const col = columnMenuState.find((c) => c.field === field);
    if (!col || !gridApi) return;
    const visibleCount = columnMenuState.filter((c) => !c.hide).length;
    if (!col.hide && visibleCount <= 1) return; // always keep at least one column visible
    gridApi.setColumnsVisible([field], col.hide);
  }

  // --- Persistence (localStorage, per-browser - see ui/CONTEXT.md) ---
  const COLUMN_STATE_STORAGE_KEY = 'wltp-health-events:gridColumnState';
  const FILTER_STORAGE_KEY = 'wltp-health-events:gridFilterModel';

  function persistColumnState() {
    try {
      localStorage.setItem(COLUMN_STATE_STORAGE_KEY, JSON.stringify(gridApi.getColumnState()));
    } catch (e) {
      // localStorage unavailable (private browsing, quota, ...) - just don't persist
    }
  }

  function restoreColumnState() {
    try {
      const stored = JSON.parse(localStorage.getItem(COLUMN_STATE_STORAGE_KEY));
      if (Array.isArray(stored) && stored.length > 0) {
        gridApi.applyColumnState({ state: stored, applyOrder: true });
        return true;
      }
    } catch (e) {
      // corrupt/inaccessible storage - fall back to colDefs' own default order/visibility
    }
    return false;
  }

  // --- Widen every column except Description to fit its full content (header + every cell's
  // value), per explicit request - COMPACT_WIDTHS above is just the initial guess before this
  // runs on first render. A saved localStorage column-state value (user-resized) always wins over
  // this, same precedent as column order/visibility elsewhere in this file. ---
  function widenAllColumns() {
    if (!gridApi) return;
    const fields = COLUMNS.map(([field]) => field).filter((field) => field !== 'description');
    gridApi.autoSizeColumns(fields, false);
  }

  function persistFilterModel() {
    try {
      if (Object.keys(filterModelSnapshot).length > 0) {
        localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterModelSnapshot));
      } else {
        localStorage.removeItem(FILTER_STORAGE_KEY);
      }
    } catch (e) {
      // localStorage unavailable (private browsing, quota, ...) - just don't persist
    }
  }

  function restoreFilterModel() {
    try {
      const stored = JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY));
      if (stored && typeof stored === 'object') {
        gridApi.setFilterModel(stored);
      }
    } catch (e) {
      // corrupt/inaccessible storage - fall back to no active filters
    }
  }

  function createGridInstance() {
    if (gridApi) {
      gridApi.destroy();
      gridApi = null;
    }
    if (!container) return;
    gridApi = createGrid(container, {
      // Deliberately NOT theme: 'legacy' - matches wltp-findings/overview's FindingsTable.svelte
      // exactly (which also doesn't set it). Omitting it does trigger AG Grid's console error #239
      // (v33's new default Theming API "clashing" with importing the old ag-theme-alpine.css file
      // directly) in both apps - harmless here, tolerated rather than fixed, since forcing
      // 'legacy' turns out to have its own real cost: it fully opts out of the new Theming API's
      // own CSS-variable injection (icon SVGs, header/row height defaults, ...), which the old
      // theme CSS file relies on for anything it doesn't hardcode itself. Confirmed by direct
      // A/B comparison against the reference app: with 'legacy' set, the header rendered ~49px
      // tall (vs. ~26px without it) and the header's filter icon was entirely invisible (its
      // --ag-icon-image-filter CSS variable, normally supplied by the new Theming API layer, was
      // never set) - both symptoms disappeared immediately once 'legacy' was removed, with every
      // other custom .ag-theme-alpine override (colors, borders, zebra rows, ...) still applying
      // correctly regardless.
      //
      // This is also why @group-ui/group-ui-assets's own official AG Grid v33 theme
      // (themes/ag-grid/groupui-ag-grid-v33.css) is deliberately NOT imported here despite being
      // available: it only maps colors onto the same legacy-only --ag-* variable names
      // (--ag-header-background-color etc.), so it only has any effect at all under
      // theme: 'legacy' - and confirmed via the same A/B test, that combination reintroduces the
      // exact invisible-filter-icon bug above (GroupUI's theme doesn't supply icon
      // fonts/images either). Colors are tokenized manually instead (see the .ag-theme-alpine
      // overrides below, now using var(--groupui-sys-color-*) instead of hardcoded hex), keeping
      // the proven-working non-legacy setup intact.
      columnDefs: buildColumnDefs(),
      rowData: events,
      defaultColDef: {
        resizable: true,
        sortable: true,
        minWidth: 80,
        menuTabs: ['filterMenuTab'],
      },
      popupParent: document.body,
      enableCellTextSelection: true,
      ensureDomOrder: true,
      // Grid fills a bounded height and scrolls internally (both vertical for rows, horizontal
      // for columns). Replaces the previous autoHeight mode where the page itself scrolled.
      domLayout: 'normal',
      getRowClass: (params) => (expandedRows.has(rowKey(params.data)) ? 'row-expanded' : undefined),
      onCellClicked: (event) => {
        // Only toggle expand/collapse when clicking the Description cell; other cells remain
        // clickable for text selection/copy without triggering expansion.
        if (event.colDef.field !== 'description') return;
        const key = rowKey(event.data);
        const next = new Set(expandedRows);
        if (next.has(key)) next.delete(key); else next.add(key);
        expandedRows = next;
        gridApi.redrawRows({ rowNodes: [event.node] });
      },
      onGridReady: () => {
        hadPersistedColumnState = restoreColumnState();
        refreshColumnMenuState();
      },
      onFirstDataRendered: () => {
        restoreFilterModel();
        if (!hadPersistedColumnState) widenAllColumns();
      },
      onColumnMoved: (e) => {
        if (e.finished) {
          refreshColumnMenuState();
          persistColumnState();
        }
      },
      onColumnVisible: (e) => {
        // A column just made visible (e.g. via the Columns menu) never had a chance to size
        // itself to its own content - autosize just that one so it doesn't keep whatever
        // arbitrary width it had while hidden.
        const shown = (e.columns || (e.column ? [e.column] : []))
          .filter((c) => c.isVisible() && c.getColId() !== 'description')
          .map((c) => c.getColId());
        if (shown.length > 0) gridApi.autoSizeColumns(shown, false);
        refreshColumnMenuState();
        persistColumnState();
      },
      onFilterChanged: () => {
        filterModelSnapshot = gridApi.getFilterModel() || {};
        activeFilterCount = Object.keys(filterModelSnapshot).length;
        persistFilterModel();
      },
    });
  }

  onMount(() => {
    createGridInstance();
  });

  // Rebuilds (destroys + recreates) the whole grid whenever a new `events` array arrives (e.g.
  // after Refresh) - mirrors wltp-findings/overview's FindingsTable.svelte exactly, including that
  // app's own trade-off: any not-yet-persisted-to-localStorage filter tweak is lost on refresh,
  // since restoreFilterModel() only replays the last *saved* snapshot, not an in-memory one.
  $: if (container && events) {
    createGridInstance();
  }

  onDestroy(() => {
    gridApi?.destroy();
  });
</script>

<div class="grid-wrapper">
  <div class="table-toolbar">
    <div class="quick-filters">
      {#each allQuickFilters as qf, i}
        {@const active = isQuickFilterActive(qf, filterModelSnapshot)}
        {#if i === QUICK_FILTERS.length && projectQuickFilters.length > 0}
          <span class="quick-filter-divider">|</span>
        {/if}
        <button
          type="button"
          class="g-chip g-chip-secondary quick-filter-btn"
          class:active
          on:click={() => toggleQuickFilter(qf)}
        >
          {qf.label}
        </button>
      {/each}
      {#if activeFilterCount > 0}
        <button type="button" class="g-chip g-chip-secondary quick-filter-btn clear" on:click={clearAllFilters}>
          Clear filters
        </button>
      {/if}
    </div>
    <div class="toolbar-actions">
      <details class="g-popover columns-menu" bind:open={columnsMenuOpen}>
        <summary class="g-btn g-btn-primary">
          Columns
          <svg
            class="toolbar-btn-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M3 6h18" />
            <path d="M7 12h10" />
            <path d="M10 18h4" />
          </svg>
        </summary>
        <div class="g-popover-content g-popover-placement-bottom-end columns-menu-panel">
          {#each columnMenuState as col (col.field)}
            <label class="col-filter-item">
              <input
                type="checkbox"
                class="g-checkbox"
                checked={!col.hide}
                on:change={() => toggleColumnVisible(col.field)}
              />
              <span>{col.label}</span>
            </label>
          {/each}
        </div>
      </details>
      <button class="g-btn g-btn-primary" on:click={onRefresh}>Refresh</button>
    </div>
  </div>
  {#if events.length === 0}
    <p class="center muted">No open Health events.</p>
  {:else}
    <div bind:this={container} class="ag-theme-alpine grid"></div>
  {/if}
</div>

<svelte:window on:click={(event) => {
  if (columnsMenuOpen && !event.target.closest('.columns-menu')) columnsMenuOpen = false;
}} />

<style>
  @import 'ag-grid-community/styles/ag-grid.css';
  @import 'ag-grid-community/styles/ag-theme-alpine.css';

  .grid-wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
  }

  .grid {
    width: 100%;
    flex: 1;
    min-height: 0;
  }

  /* ── Toolbar (quick filters left, Columns/Refresh right) ── */
  .table-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .quick-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  .toolbar-actions .g-btn {
    font-size: 13px;
    padding: 3px 11px;
    line-height: 18px;
  }

  .toolbar-actions summary.g-btn {
    font-size: 13px;
    padding: 3px 11px;
    line-height: 18px;
  }

  /* Base look (secondary/outline: transparent bg, teal border+text) comes from GroupUI's own
     .g-chip/.g-chip-secondary classes (groupui.css) - only the toggled "active" state is custom,
     since the CSS framework's chip has no built-in selected/toggle state of its own. !important
     needed since groupui.css's own .g-chip-secondary rule (using a :not() pseudo-class, which
     counts toward specificity) otherwise outranks a plain two-class selector here. */
  .quick-filter-btn {
    font-size: 13px;
    padding: 2px 9px;
    line-height: 20px;
  }

  .quick-filter-btn.active {
    background-color: var(--groupui-sys-color-background-inform) !important;
    color: var(--groupui-sys-color-text-normal-inverse) !important;
    border-color: var(--groupui-sys-color-background-inform) !important;
  }

  /* Matches wltp-findings/overview's own .tab-divider exactly (there, between its "In Escalation"
     tab and the ones before it; here, between the fixed RDS/ECS service shortcuts and the
     per-project chips derived from the loaded events - see the template above). */
  .quick-filter-divider {
    font-weight: bold;
    color: var(--groupui-sys-color-brand-core);
    font-size: 1.1rem;
    user-select: none;
    margin: 0 0.15rem;
  }

  /* Same 3-horizontal-line "filter" glyph AG Grid itself uses in the table header (its default
     ag-icon-filter icon) - reused here for visual consistency with the Columns menu's own
     show/hide-columns purpose. Sized to match .g-btn's own icon conventions; color left as
     currentColor (matches .g-btn-primary's own white text) rather than the Electric Neon used
     elsewhere - that pairing was specific to this button's previous solid-petrol look, which
     .g-btn-primary's own (teal) action color no longer matches. */
  .toolbar-btn-icon {
    width: 11px;
    height: 11px;
    color: #c2fe06;
    flex-shrink: 0;
  }

  /* ── Columns show/hide menu ── */
  .columns-menu {
    position: relative;
  }

  /* Layered on top of GroupUI's own .g-popover-content (groupui.css) - just the size/scroll
     behavior this app needs for a potentially-long column list; background/border/shadow/
     positioning all come from the framework class itself. */
  .columns-menu-panel {
    width: 200px;
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .col-filter-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.2rem 0.3rem;
    border-radius: 4px;
    font-size: 0.82rem;
    cursor: pointer;
  }

  .col-filter-item:hover {
    background: var(--groupui-sys-color-action-subtle-hover);
  }

  /* ── Root wrapper ── */
  :global(.ag-theme-alpine .ag-root-wrapper) {
    /* Also overrides the --ag-header-height CSS variable itself (in case AG's own internal
       layout math reads it directly elsewhere) - the actual visible height fix is the
       !important block further below though (see its own comment for why). */
    --ag-header-height: 26px;
    border: none;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    font-family: var(--groupui-sys-font-family-primary), system-ui, sans-serif;
  }

  /* ── Header ── */
  /* AG Grid v33's Theming API measures --ag-header-height once at layout time (via a
     getComputedStyle read on an ancestor wrapper, before .ag-header-row's own inline height gets
     set) and then bakes the result into an inline height style on .ag-header/.ag-header-row -
     overriding the CSS variable alone (see the .ag-root-wrapper rule above) only fixes what the
     variable itself resolves to, not that already-baked inline style. Forcing the actual height
     with !important here is the reliable fix regardless of AG's internal measurement timing. The
     header's own text (0.8rem, below) renders at a ~16px line height - 5px top + 5px bottom
     around that, per explicit request. */
  :global(.ag-theme-alpine .ag-header),
  :global(.ag-theme-alpine .ag-header-viewport),
  :global(.ag-theme-alpine .ag-header-container),
  :global(.ag-theme-alpine .ag-header-row),
  :global(.ag-theme-alpine .ag-header-cell),
  :global(.ag-theme-alpine .ag-header-cell-comp-wrapper),
  :global(.ag-theme-alpine .ag-header-cell-resize) {
    height: 26px !important;
    min-height: 26px !important;
  }

  :global(.ag-theme-alpine .ag-header) {
    background: var(--groupui-sys-color-brand-core);
    border-bottom: 2px solid var(--groupui-sys-color-hero-basic-default);
  }

  :global(.ag-theme-alpine .ag-header-cell) {
    color: var(--groupui-sys-color-text-normal-inverse);
    font-weight: 500;
    font-size: 0.8rem;
    letter-spacing: 0.02em;
    border-right: 1px solid #1b3e4a !important;
  }

  :global(.ag-theme-alpine .ag-header-cell:last-child) {
    border-right: none !important;
  }

  :global(.ag-theme-alpine .ag-header-cell:hover) {
    background: #1b3e4a !important;
  }

  :global(.ag-theme-alpine .ag-header-cell-sorted-asc),
  :global(.ag-theme-alpine .ag-header-cell-sorted-desc) {
    background: #1b3e4a !important;
  }

  :global(.ag-theme-alpine .ag-icon) {
    color: var(--groupui-sys-color-hero-basic-default);
  }

  :global(.ag-theme-alpine .ag-header-icon) {
    color: var(--groupui-sys-color-hero-basic-default);
  }

  :global(.ag-theme-alpine .ag-sort-indicator-icon .ag-icon) {
    color: var(--groupui-sys-color-hero-basic-default) !important;
  }

  /* ── Floating filter row ── */
  :global(.ag-theme-alpine .ag-floating-filter) {
    display: none;
  }

  /* ── Header menu / filter icon ── */
  :global(.ag-theme-alpine .ag-header-cell-menu-button) {
    opacity: 0.6;
    transition: opacity 0.15s;
  }

  :global(.ag-theme-alpine .ag-header-cell-menu-button:hover) {
    opacity: 1;
  }

  :global(.ag-theme-alpine .ag-header-cell-menu-button .ag-icon) {
    color: var(--groupui-sys-color-hero-basic-default);
  }

  /* ── Rows ── */
  :global(.ag-theme-alpine .ag-row) {
    font-size: 0.82rem;
    border-color: var(--groupui-sys-color-line-weak);
    transition: background 0.1s;
  }

  :global(.ag-theme-alpine .ag-row-odd) {
    /* Literally GroupUI's own token for striped-table rows (--groupui-sys-color-background-stripe)
       - a semi-transparent light gray, layered over the row-even background below. */
    background: var(--groupui-sys-color-background-stripe);
  }

  :global(.ag-theme-alpine .ag-row-even) {
    background: var(--groupui-sys-color-background-base);
  }

  :global(.ag-theme-alpine .ag-row:hover) {
    background: var(--groupui-sys-color-action-subtle-hover) !important;
  }

  /* Row expanded to show the full Description text (see descriptionCellRenderer) - a stronger
     tint than the plain hover above (same teal family, "pressed" rather than "hover"), distinct
     from both the normal odd/even row backgrounds and the hover tint above. */
  :global(.ag-theme-alpine .row-expanded) {
    background: var(--groupui-sys-color-action-subtle-pressed) !important;
  }

   /* ── Cells ── */
   :global(.ag-theme-alpine .ag-cell) {
     line-height: 1.6;
     padding-left: 12px;
     padding-right: 12px;
     border-right: 1px solid var(--groupui-sys-color-line-weak);
   }

   :global(.ag-theme-alpine .ag-cell:last-child) {
     border-right: none;
   }

   /* Description cell is the only cell where clicking toggles row expansion */
   :global(.ag-theme-alpine .description-cell) {
     cursor: pointer;
   }

  /* ── Filter popup ── */
  :global(.ag-popup),
  :global(.ag-popup-child),
  :global(.ag-filter),
  :global(.ag-menu),
   :global(.ag-column-menu),
   :global(.ag-popup-editor) {
     z-index: 9999 !important;
   }

   :global(.ag-theme-alpine .ag-body-viewport) {
     overflow-x: auto;
     overflow-y: auto;
   }

  /* ── Set filter styles (for SetFilter.js) ── */
  :global(.set-filter) {
    padding: 12px;
    min-width: 200px;
    max-height: 350px;
    display: flex;
    flex-direction: column;
    font-size: 13px;
    font-family: var(--groupui-sys-font-family-primary), system-ui, sans-serif;
    background: var(--groupui-sys-color-background-base);
    border-radius: 6px;
  }
  :global(.set-filter-search) {
    margin-bottom: 8px;
  }
  :global(.set-filter-input) {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid var(--groupui-sys-color-line-weak);
    border-radius: 4px;
    font-size: 13px;
    font-family: var(--groupui-sys-font-family-primary), system-ui, sans-serif;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }
  :global(.set-filter-input:focus) {
    outline: none;
    border-color: var(--groupui-sys-color-action-basic-default);
    box-shadow: 0 0 0 2px var(--groupui-sys-color-action-subtle-pressed);
  }
  :global(.set-filter-actions) {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--groupui-sys-color-line-weak);
  }
  :global(.set-filter-link) {
    font-size: 12px;
    color: var(--groupui-sys-color-action-basic-default);
    text-decoration: none;
    font-weight: 500;
  }
  :global(.set-filter-link:hover) {
    text-decoration: underline;
  }
  :global(.set-filter-list) {
    overflow-y: auto;
    flex: 1;
  }
  :global(.set-filter-item) {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 4px;
    cursor: pointer;
    border-radius: 4px;
  }
  :global(.set-filter-item:hover) {
    background: var(--groupui-sys-color-action-subtle-hover);
  }
  :global(.set-filter-item input) {
    margin: 0;
    cursor: pointer;
    accent-color: var(--groupui-sys-color-action-basic-default);
  }
  :global(.set-filter-item span) {
    user-select: none;
  }
</style>
